import React from 'react'

import { FeatureStoryline, FeatureStorylineFormSchema } from 'codecs/storyline/FeatureStoryline'
import Container from 'shared/components/Container'
import Button from 'shared/components/Button'
import Text from 'shared/components/Text'
import { StorylineForm } from './StorylineForm'
import SearchInput from 'shared/components/SearchInput'
import TableHeaderContent from 'shared/components/Table/TableHeaderContent'
import { useContextHelper } from 'shared/hooks/useContextHelper'
import {
    useStorylinesReducer,
    createStorylineContext,
} from 'shared/storylines/hooks/useStorylinesReducer'
import { warnOnValidationErrors } from 'shared/helper/warnOnValidationErrors'

import { StorylinesList } from './StorylinesList'
import {
    useFormControlsEnabled,
    useViewMode,
    getWIPData,
    useWIPStorylinesActor,
} from 'shared/resource/ResourceMachineProvider'

const StorylinesContext = createStorylineContext<FeatureStoryline>()

// eslint-disable-next-line
export function useStorylinesContext() {
    return useContextHelper(StorylinesContext, 'StorylinesContext')
}

const Storylines: React.FC = (): React.ReactElement => {
    const [wipState, send] = useWIPStorylinesActor<FeatureStoryline[]>()
    const formControlsEnabled = useFormControlsEnabled('storylines')
    const storylines = getWIPData(useViewMode(), wipState)

    const [searchText, setSearchText] = React.useState('')
    const [state, actions] = useStorylinesReducer<FeatureStoryline>({
        storylines,
        isCreatingNew: false,
        editIdentifier: null,
        shouldSave: false,
        lastInsertedStorylineIdentifier: null,
    })

    // when props storylines change, we need to update our state storylines
    React.useEffect(() => {
        actions.setList(storylines)
    }, [storylines, actions])

    // The context we provide is our state + bound action creators.
    const storylinesContextValue = React.useMemo(() => {
        return {
            ...state,
            ...actions,
            save: (s: FeatureStoryline) => {
                warnOnValidationErrors('Storyline warnings', FeatureStorylineFormSchema, s)
                return actions.save(s)
            },
            formControlsEnabled,
        }
    }, [formControlsEnabled, state, actions])

    // when state.shouldSave is set to true, we fire of a request for update
    // to the data manager
    React.useEffect(() => {
        const { shouldSave, storylines } = state
        if (!shouldSave) {
            return
        }

        send({
            type: 'UPDATE',
            data: storylines,
        })

        actions.savePerformed()
    }, [send, state, actions])

    return (
        <StorylinesContext.Provider value={storylinesContextValue}>
            <Container alignItems="center">
                <TableHeaderContent>
                    <Text as="h3" size="2" my="2">
                        Storylines
                    </Text>

                    <SearchInput value={searchText} onChange={e => setSearchText(e.target.value)} />
                </TableHeaderContent>

                <OptionalAddNewStorylineButton />
            </Container>

            <OptionalNewStorylineForm />

            <StorylinesList searchText={searchText} />
        </StorylinesContext.Provider>
    )
}

const OptionalAddNewStorylineButton: React.FC = (): React.ReactElement | null => {
    const { formControlsEnabled, isCreatingNew, createNew } = useStorylinesContext()

    if (!formControlsEnabled) {
        return null
    }

    return (
        <Button disabled={isCreatingNew} variant="primary" onClick={() => createNew()}>
            + Add Storyline
        </Button>
    )
}

const OptionalNewStorylineForm: React.FC = (): React.ReactElement | null => {
    const { formControlsEnabled, isCreatingNew } = useStorylinesContext()

    if (!formControlsEnabled || !isCreatingNew) {
        return null
    }

    return <StorylineForm storyline={null} />
}

export default React.memo(Storylines)
